/**
 * @type {URLSearchParams}
 */
let urlSearchParams;

/**
 * Retrieves a parameter from the URL.
 * If the parameter does not exist, returns the given default value.
 *
 * @param {String} name
 * @param {String | null} defaultValue
 * @returns {String | null}
 */
export function getParam(name, defaultValue = null) {
  if (!urlSearchParams) {
    urlSearchParams = new URLSearchParams(location.search);
  }
  return urlSearchParams.get(name) ?? defaultValue;
}

/**
 * Adds a parameter to the URL.
 *
 * @param {String} name
 * @param {String} value
 */
export function setParam(name, value) {
  const url = new URL(window.location);
  url.searchParams.set(name, value);
  window.history.replaceState(history.state, "", url.toString());
}

/**
 * Clears the whole query string from the URL.
 *
 * Used to remove query parameters from the URL because when they were already
 * consumed by the backend to avoid state desync.
 *
 * Example of a broken flow if we don't do that:
 *   1. User loads the edit/new page for a training session
 *   2. User starts filling the form
 *   3. User clicks on a link to edit a place (the form is serialized to the
 *      query string of the back_path)
 *   4. User fills the form to edit the place
 *   5. User saves and is redirected to the training session edit page
 *   6. The backend restores the form state from the query string (query
 *      parameters are consumed by the backend)
 *   7. Due to previous state in the query string, if the user continues edition we have desync
 *   8. The user saves the page, they're now redirected to the show page, then clicks the back button
 *   9. The user is back to the edit page, but the query string is still
 *      there, so the form is restored to the state of step 2
 */
export function clearQueryString(options = {}) {
  const { allow_list = ["back_path"] } = options;
  const url = new URL(window.location);
  for (const key of Array.from(url.searchParams.keys())) {
    if (!allow_list.includes(key)) {
      url.searchParams.delete(key);
    }
  }
  history.replaceState(history.state, "", url.toString());
}

/**
 * Creates a backpath from a form, that serializes the form fields into the URL.
 */
export function createBackpath(form, select) {
  const formData = new FormData(form);
  const url = new URL(window.location);
  for (const [key, value] of formData) {
    if (!key.startsWith(`${select}[`)) {
      continue;
    }
    url.searchParams.set(key, value);
  }
  return url.pathname + url.search + url.hash;
}

export function initForAlpine(Alpine) {
  Alpine.magic("query", () => {
    return {
      getParam,
      setParam,
      clearQueryString,
      createBackpath,
    };
  });
}
